/**
 * Shared body-drag for two-anchor drawings (fib, price range, …).
 *
 * Moving a two-anchor drawing as a whole is identical for every such tool, and
 * must translate in **logical (bar-index) space, not time**: the chart collapses
 * gaps (weekends/holidays), so a fixed *time* delta distorts the shape when an
 * edge lands in a gap (it sticks at the gap boundary while the other edge moves —
 * looks like a resize). Translating by bar delta keeps the shape across gaps.
 *
 * Two-anchor tools MUST use these instead of hand-rolling drag handlers:
 *   - `startBodyDrag(e, ctx, kind, drawing)`            in the renderer onMouseDown
 *   - `bodyDragPartial(drag.initial, logical, price, candles)` in the move loop
 */
import type { MouseEvent, MutableRefObject, RefObject } from "react";
import type { IChartApi } from "lightweight-charts";
import { setSelectedDrawing } from "@/state/slices/uiSlice";
import type { AppDispatch } from "@/state/store";
import {
  type AnySeries,
  timeToLogical,
  logicalToTime,
} from "@/components/Chart/chartData";
import type { Candle } from "@/api/types";
import type { DragTarget } from "@/components/Chart/types";

/** Drag kinds that support a whole-drawing "body" move (two-anchor tools). */
export type BodyDragKind = "fib" | "priceRange";

/** The four time/price fields shared by every two-anchor drawing. */
export interface TwoAnchorDrawing {
  id: string;
  startTime: number;
  startPrice: number;
  endTime: number;
  endPrice: number;
}

/** The chart refs + dispatch a renderer must pass through to start a body drag.
 *  Tool render-contexts (FibRenderCtx, PriceRangeRenderCtx) are supersets of
 *  this, so they satisfy it structurally. */
export interface BodyDragCtx {
  seriesRef: MutableRefObject<AnySeries | null>;
  chartRef: MutableRefObject<IChartApi | null>;
  containerRef: RefObject<HTMLDivElement>;
  dragRef: MutableRefObject<DragTarget | null>;
  dispatch: AppDispatch;
  tabId: string;
  setIsDragging: (v: boolean) => void;
}

/** Begin a whole-drawing MOVE from any of its lines. Snapshots both anchors +
 *  the mousedown point (the cursor's LOGICAL position + price), selects the
 *  drawing, and suppresses chart pan. Left-button only. */
export function startBodyDrag(
  e: MouseEvent,
  ctx: BodyDragCtx,
  kind: BodyDragKind,
  d: TwoAnchorDrawing,
): void {
  if (e.button !== 0) return;
  e.stopPropagation();
  e.preventDefault();
  const containerEl = ctx.containerRef.current;
  const chartApi = ctx.chartRef.current;
  const seriesApi = ctx.seriesRef.current;
  if (!containerEl || !chartApi || !seriesApi) return;
  const r = containerEl.getBoundingClientRect();
  const anchorLogical = chartApi
    .timeScale()
    .coordinateToLogical(e.clientX - r.left);
  const anchorPrice = seriesApi.coordinateToPrice(e.clientY - r.top);
  if (anchorLogical == null || anchorPrice == null) return;

  ctx.dispatch(
    setSelectedDrawing({ tabId: ctx.tabId, selected: { kind, id: d.id } }),
  );
  ctx.setIsDragging(true);
  const initial = {
    startTime: d.startTime,
    startPrice: d.startPrice,
    endTime: d.endTime,
    endPrice: d.endPrice,
    anchorLogical: anchorLogical as number,
    anchorPrice,
  };
  // Concrete per-kind targets so the discriminated union stays type-safe.
  ctx.dragRef.current =
    kind === "fib"
      ? { type: "fib", id: d.id, anchor: "body", initial }
      : { type: "priceRange", id: d.id, anchor: "body", initial };
}

/** Snapshot carried by a body drag: both anchors + the mousedown cursor (logical
 *  bar position + price). */
export interface BodyDragInitial {
  startTime: number;
  startPrice: number;
  endTime: number;
  endPrice: number;
  anchorLogical: number;
  anchorPrice: number;
}

/** Shift a single anchor time by a logical (bar) delta, gap-correct: convert the
 *  time → logical, add the bar delta, convert back. This is the core of every
 *  body drag — the chart collapses gaps, so translating in logical space keeps a
 *  drawing's shape constant across weekends/holidays where a plain *time* delta
 *  would distort it. Any tool whose body drag moves times MUST shift through this
 *  (two-anchor tools via `bodyDragPartial`, position via its own field math).
 *  Returns null when the shifted bar maps off the data. */
export function shiftTimeByLogical(
  time: number,
  dLogical: number,
  candles: Candle[],
): number | null {
  return logicalToTime(timeToLogical(time, candles) + dLogical, candles);
}

/** Translate both anchors by the cursor delta for one move tick. The TIME delta
 *  is applied in LOGICAL (bar) space via `shiftTimeByLogical` so the shape stays
 *  constant across chart gaps. Price is continuous (no gaps), so it's a plain
 *  delta. Returns null when there's nothing to apply (cursor off the data/price
 *  area). */
export function bodyDragPartial(
  initial: BodyDragInitial,
  currentLogical: number | null,
  price: number | null,
  candles: Candle[],
): Pick<TwoAnchorDrawing, "startTime" | "startPrice" | "endTime" | "endPrice"> | null {
  if (currentLogical == null || price == null) return null;
  const dLogical = currentLogical - initial.anchorLogical;
  const startTime = shiftTimeByLogical(initial.startTime, dLogical, candles);
  const endTime = shiftTimeByLogical(initial.endTime, dLogical, candles);
  if (startTime == null || endTime == null) return null;
  const dp = price - initial.anchorPrice;
  return {
    startTime,
    endTime,
    startPrice: initial.startPrice + dp,
    endPrice: initial.endPrice + dp,
  };
}
